import { FC, useEffect, useId, useRef, useState } from "react";
import { Search } from "lucide-react";
import { useDebounce } from "src/libs/hooks";
import { AxiosError } from "axios";
import useStore from "src/hooks/useStore/useStore";
import StoreSearchItem from "src/components/StoreSearchItem/StoreSearchItem";
import ResultCountAnnouncer from "src/components/ResultCountAnnouncer/ResultCountAnnouncer";

interface Props {
	value?: string;
	onChange: (user: Partial<Store>) => void;
}

/**
 * Type-ahead picker for retail stores. Implements the WAI-ARIA
 * combobox-with-listbox pattern (combobox/listbox/option roles, full
 * keyboard navigation including ArrowDown-to-reopen, Home/End,
 * Enter, Escape) and is functionally identical to its sibling
 * `containers/UserSearch` and the reporting EmployeeSearch /
 * reporting StoreSearch.
 *
 * Notes for the next reader:
 *  - This is a CONTROLLED picker (parent owns `value`). The reporting
 *    pickers own their own input state. That distinction matters
 *    because, after a selection, the parent mirrors the picked
 *    store's name back through `value` and the debounce loop would
 *    otherwise reopen the popup ~300ms later — `lastSelectedName`
 *    suppresses that re-open until the user actually types something
 *    new.
 *  - Options are `<div role="option">` (with `onMouseDown`
 *    preventDefault) rather than `<button role="option">` so AT
 *    don't leak the underlying button role and so mouse clicks
 *    don't steal focus from the combobox input.
 *  - The listbox container is rendered whenever `isPopupOpen`, even
 *    when results are empty — keeps `aria-expanded` honest about
 *    the element it `aria-controls`.
 */
const StoreSearch: FC<Props> = ({ value, onChange }) => {
	const [debouncedSearchValue, setDebouncedSearchValue] = useState<
		string | undefined
	>();
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);

	const listInnerRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	// Last picked name. Suppresses the auto-open effect after a
	// selection re-flows through the controlled `value` prop.
	const lastSelectedNameRef = useRef<string | null>(null);
	const listboxId = useId();

	const {
		data,
		isError,
		error,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage
	} = useStore(debouncedSearchValue, {
		enabled: Boolean(debouncedSearchValue)
	});

	useDebounce(() => setDebouncedSearchValue(value), 300, [value]);

	const stores = data ? data.pages.flatMap(({ results }) => results) : [];
	const isLoading = isFetching && Boolean(debouncedSearchValue);
	// `isPopupOpen` reflects whether the listbox is mounted, so
	// `aria-expanded` and `aria-controls` stay honest. We render the
	// listbox container even when there are zero results.
	const isPopupOpen = isOpen && Boolean(debouncedSearchValue);

	// Reset the keyboard cursor (and auto-open) when the *query* changes,
	// but not when `data` does — infinite-scroll page loads must not
	// wipe the user's highlighted row.
	useEffect(() => {
		setActiveIndex(-1);
		// If the user typed something different to the last selection,
		// release the suppression so the popup can re-open.
		if (debouncedSearchValue !== lastSelectedNameRef.current) {
			lastSelectedNameRef.current = null;
		}
		if (
			debouncedSearchValue &&
			debouncedSearchValue !== lastSelectedNameRef.current
		) {
			setIsOpen(true);
		} else {
			setIsOpen(false);
		}
	}, [debouncedSearchValue]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
				// Stale aria-activedescendant on next reopen otherwise.
				setActiveIndex(-1);
			}
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const handleScroll = () => {
		if (listInnerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = listInnerRef.current;
			// `>= - 1` (instead of strict equality) — sub-pixel scroll on
			// macOS / hi-DPI / browser zoom can leave fractional gaps that
			// prevent an exact match and silently kill infinite scroll.
			if (scrollTop + clientHeight >= scrollHeight - 1) {
				hasNextPage && fetchNextPage();
			}
		}
	};

	const handleSelect = (store: Store) => {
		lastSelectedNameRef.current = store.name;
		onChange(store);
		setIsOpen(false);
		setActiveIndex(-1);
		// Keep focus on the input — APG requires focus to remain on the
		// combobox throughout the interaction.
		inputRef.current?.focus();
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape" && isPopupOpen) {
			e.preventDefault();
			setIsOpen(false);
			setActiveIndex(-1);
			return;
		}

		// ArrowDown reopens the popup if the user closed it via Escape
		// while a query is still present. APG-conformant behaviour.
		if (
			e.key === "ArrowDown" &&
			!isPopupOpen &&
			debouncedSearchValue &&
			stores.length > 0
		) {
			e.preventDefault();
			setIsOpen(true);
			setActiveIndex(0);
			return;
		}

		if (!isPopupOpen || stores.length === 0) return;

		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActiveIndex((prev) => (prev < stores.length - 1 ? prev + 1 : 0));
				break;
			case "ArrowUp":
				e.preventDefault();
				setActiveIndex((prev) => (prev > 0 ? prev - 1 : stores.length - 1));
				break;
			case "Home":
				e.preventDefault();
				setActiveIndex(0);
				break;
			case "End":
				e.preventDefault();
				setActiveIndex(stores.length - 1);
				break;
			case "Enter":
				e.preventDefault();
				if (activeIndex >= 0 && activeIndex < stores.length) {
					handleSelect(stores[activeIndex]);
				}
				break;
		}
	};

	const activeOptionId =
		activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;

	if (isError && error)
		return <p className="text-red-600">{(error as AxiosError).message}</p>;

	return (
		<div ref={containerRef} className="relative">
			<div className="relative">
				<Search
					aria-hidden="true"
					className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
				/>
				<input
					ref={inputRef}
					type="text"
					placeholder="Search by store name..."
					value={value}
					onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
						onChange({ name: target.value });
						if (target.value) setIsOpen(true);
					}}
					onFocus={() => debouncedSearchValue && setIsOpen(true)}
					onKeyDown={handleKeyDown}
					role="combobox"
					aria-expanded={isPopupOpen}
					aria-haspopup="listbox"
					aria-autocomplete="list"
					aria-controls={listboxId}
					aria-activedescendant={activeOptionId}
					aria-label="Search by store name"
					className="w-full bg-white border border-border rounded-lg pl-9 pr-10 py-2 text-sm shadow-xs focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
				/>

				{isFetchingNextPage && (
					<span className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin block" />
				)}
			</div>

			<ResultCountAnnouncer
				isSearching={isLoading}
				count={stores.length}
				isOpen={isPopupOpen}
				subjectSingular="store"
				subjectPlural="stores"
				query={debouncedSearchValue ?? ""}
			/>

			{isPopupOpen && (
				<div
					ref={listInnerRef}
					onScroll={handleScroll}
					id={listboxId}
					role="listbox"
					aria-label="Retail stores"
					className="absolute z-10 mt-1 bg-white border border-border rounded-xl shadow-md max-h-60 overflow-y-auto w-full"
				>
					{stores.length === 0 && !isLoading ? (
						<div className="px-3 py-3 text-sm text-muted-foreground">
							No stores found
						</div>
					) : (
						stores.map((store, index) => (
							<div
								key={`option-${store.id}`}
								id={`${listboxId}-option-${index}`}
								role="option"
								aria-selected={index === activeIndex}
								// onMouseDown preventDefault keeps focus on the
								// combobox input — see the focus-management note
								// at the top of the file.
								onMouseDown={(e) => e.preventDefault()}
								onClick={() => handleSelect(store)}
								className={[
									"w-full text-left px-3 py-2.5 text-sm text-foreground transition-colors first:rounded-t-xl last:rounded-b-xl cursor-pointer",
									index === activeIndex
										? "bg-primary-lighter"
										: "hover:bg-primary-lighter"
								].join(" ")}
							>
								<StoreSearchItem store={store} icon={null} />
							</div>
						))
					)}
				</div>
			)}
		</div>
	);
};

export default StoreSearch;
